import { t, useI18n } from "@bookingcom/lingojs-react";
import { FILTERS } from "../../../constants.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getSearchSegments } from "../../../store/search/selectors.js";
import { getSearchAvailableFilters, getSearchFetchStatus, getSearchResultsError, getSearchResultsFlights, getSearchResultsBanners, getSearchResultsSubsidizedFaresSummary, getSearchResultsFetchedAt, getSearchResultsSearchCriteria } from "../../../store/searchResults/selectors.js";
import { getSearchAvailableFilters as getOBSearchAvailableFilters, getSearchFetchStatus as getOBSearchFetchStatus, getSearchResultsError as getOBSearchResultsError, getSearchResultsFlights as getOBSearchResultsFlights, getSearchResultsBanners as getOBSearchResultsBanners, getSearchResultsSubsidizedFaresSummary as getOBSearchResultsSubsidizedFaresSummary, getSearchResultsFetchedAt as getOBSearchResultsFetchedAt, getSearchResultsSearchCriteria as getOBSearchResultsSearchCriteria } from "../../../store/searchResultsBounds/outboundSelectors.js";
import { getSearchAvailableFilters as getIBSearchAvailableFilters, getSearchFetchStatus as getIBSearchFetchStatus, getSearchResultsError as getIBSearchResultsError, getSearchResultsFlights as getIBSearchResultsFlights, getSearchResultsBanners as getIBSearchResultsBanners, getSearchResultsSubsidizedFaresSummary as getIBSearchResultsSubsidizedFaresSummary, getSearchResultsFetchedAt as getIBSearchResultsFetchedAt, getSearchResultsSearchCriteria as getIBSearchResultsSearchCriteria } from "../../../store/searchResultsBounds/inboundSelectors.js";
import { getExpandedSearchAirports } from "../../../store/selectors/expandedSearch.js";
import useSearchFilters from "../../../hooks/useSearchFilters.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useGetSearchResultsBound from "../../../hooks/useGetSearchResultsBound.js";
import { isSirfAvailable } from "../../../store/selectors/sirf.js";
import { useGetFiltersDraft } from "./components/boundsFilterHooks.js";
import { actions as searchActions } from "../../../store/search/actions.js";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters.js";
const useSearchResultsDesktopController = () => {
    const bound = useGetSearchResultsBound();
    const boundSelectors = useMemo(() => {
        if (bound === "outbound") {
            return {
                availableFilters: getOBSearchAvailableFilters,
                fetchStatus: getOBSearchFetchStatus,
                error: getOBSearchResultsError,
                flights: getOBSearchResultsFlights,
                banners: getOBSearchResultsBanners,
                subsidizedFaresSummary: getOBSearchResultsSubsidizedFaresSummary,
                fetchedAt: getOBSearchResultsFetchedAt,
                searchCriteria: getOBSearchResultsSearchCriteria
            };
        }
        if (bound === "inbound") {
            return {
                availableFilters: getIBSearchAvailableFilters,
                fetchStatus: getIBSearchFetchStatus,
                error: getIBSearchResultsError,
                flights: getIBSearchResultsFlights,
                banners: getIBSearchResultsBanners,
                subsidizedFaresSummary: getIBSearchResultsSubsidizedFaresSummary,
                fetchedAt: getIBSearchResultsFetchedAt,
                searchCriteria: getIBSearchResultsSearchCriteria
            };
        }
        return {
            availableFilters: getSearchAvailableFilters,
            fetchStatus: getSearchFetchStatus,
            error: getSearchResultsError,
            flights: getSearchResultsFlights,
            banners: getSearchResultsBanners,
            subsidizedFaresSummary: getSearchResultsSubsidizedFaresSummary,
            fetchedAt: getSearchResultsFetchedAt,
            searchCriteria: getSearchResultsSearchCriteria
        };
    }, [bound]);
    const flights = useSelector(boundSelectors.flights);
    const availableFilters = useSelector(boundSelectors.availableFilters);
    const error = useSelector(boundSelectors.error);
    const searchSegments = useSelector(getSearchSegments);
    const filtersDraftWithBounds = useGetFiltersDraft();
    const fetchStatus = useSelector(boundSelectors.fetchStatus);
    const banners = useSelector(boundSelectors.banners);
    const subsidizedFaresSummary = useSelector(boundSelectors.subsidizedFaresSummary);
    const fetchedAt = useSelector(boundSelectors.fetchedAt);
    const searchCriteria = useSelector(boundSelectors.searchCriteria);
    const appliedFilters = useAppliedFilters();
    const i18n = useI18n();
    const history = useHistory();
    const location = useLocation();
    const { createUrl } = useCreateUrl();
    const { applyFilters } = useSearchFilters();
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    useEffect(() => {
        applyFilters(filtersDraftWithBounds);
    }, [applyFilters, filtersDraftWithBounds]);
    useEffect(() => {
        if (["loading", "initial"].includes(fetchStatus))
            return;
        if (flights.length === 0)
            trackV2("show_empty_search_results");
    }, [flights, fetchStatus, trackV2]);
    const expandedSearchAirports = useMemo(() => {
        const { from, to } = searchSegments[0];
        return getExpandedSearchAirports(from, to);
    }, [searchSegments]);
    const noFlightsFound = (error && "code" in error && error.code === "SEARCH_SEARCHFLIGHTS_NO_FLIGHTS_FOUND") || flights.length === 0;
    const getScreenReaderAnnouncementText = () => {
        if (appliedFilters.length > 0 && flights) {
            return i18n.trans(t("a11y_flights_filters_general_updated"));
        }
        if (appliedFilters.length === 0 && flights) {
            return i18n.trans(t("a11y_flights_sr_results_loaded"));
        }
        return "";
    };
    const onExpandToCitySearch = useCallback(() => {
        if (expandedSearchAirports) {
            const { fromCities, toCities, fromLocationName, toLocationName } = expandedSearchAirports;
            const params = new URLSearchParams(location.search);
            params.set("from", fromCities);
            params.set("to", toCities);
            params.set("fromLocationName", fromLocationName);
            params.set("toLocationName", toLocationName);
            const boundsUrl = bound ? "outbound/" : "";
            history.replace(createUrl(`/flights/${fromCities}-${toCities}/${boundsUrl}?${params}`));
            window.location.reload();
        }
    }, [expandedSearchAirports, location.search, bound, history, createUrl]);
    const dispatch = useDispatch();
    return {
        getScreenReaderAnnouncementText,
        appliedFilters,
        isFirstLoad: !availableFilters.durationMin,
        noFlightsFound,
        expandedSearchAirports,
        canExtendSearch: appliedFilters.length === 0 && noFlightsFound && expandedSearchAirports,
        onExpandToCitySearch,
        hasFlights: flights.length > 0,
        canRemoveDirectFlightsFilter: appliedFilters.includes(FILTERS.stops) && noFlightsFound,
        flights,
        fetchStatus,
        banners,
        isSirfAvailable: isSirfAvailable(subsidizedFaresSummary),
        fetchedAt,
        searchCriteria,
        resetInboundFilters: () => dispatch(searchActions.resetFiltersDraftInbound())
    };
};
export default useSearchResultsDesktopController;
